import { pipe } from 'fp-ts/function';
import { nonEmptyArray } from 'fp-ts';
import { NonEmptyArray } from 'fp-ts/NonEmptyArray';
import { AppClockService, DateTimeService } from '@mews/framework';
import { MatrixCellColor, MatrixMapping } from '@mews/b2b-ui';
import { SellLimitPerTimeUnitData } from '@mews-commander/data-access';
import { Optional } from '@mews/std';
import { SellLimitMatrixRowData } from '../../types';
import { isDateInPast } from './isDateInPast';
import { ResourceCategoryCell, SellLimitCell, TimeUnitCell } from './cells';

export interface CellClickHandlerCellData {
    timeUnit: string;
    resourceCategoryId: SellLimitMatrixRowData['resourceCategoryId'];
}

export type CellClickHandler = (
    e: Parameters<Required<MatrixMapping<SellLimitMatrixRowData>>['onCellClick']>[0],
    cellData: CellClickHandlerCellData
) => void;

interface CreateSellLimitsMatrixMappingParams {
    appClockService: AppClockService;
    onCellClick: CellClickHandler;
    rowData: NonEmptyArray<SellLimitMatrixRowData>;
    timezone: string;
    selectedCellData: Optional<CellClickHandlerCellData>;
}

export const createSellLimitsMatrixMapping = ({
    appClockService,
    onCellClick,
    rowData,
    timezone,
    selectedCellData,
}: CreateSellLimitsMatrixMappingParams): NonEmptyArray<MatrixMapping<SellLimitMatrixRowData>> => {
    const columnHeaders = pipe(rowData, nonEmptyArray.head, ({ data }) => data, Object.keys);
    const currentDateTime = DateTimeService.fromUtc(appClockService.getAppNowUtc(), timezone);
    const currentDate = DateTimeService.toDate(currentDateTime);

    return [
        {
            valueKey: 'id',
            title: ' ',
            render: ({ usableResources, resourceCategoryNames }) => (
                <ResourceCategoryCell names={resourceCategoryNames} usableResources={usableResources} />
            ),
        },
        ...columnHeaders.map(
            (timeUnit): MatrixMapping<SellLimitMatrixRowData> => ({
                valueKey: timeUnit as keyof SellLimitMatrixRowData,
                title: <TimeUnitCell utc={timeUnit} />,
                onCellClick: isDateInPast({ currentDate, dateTimeUtc: timeUnit, timezone })
                    ? undefined
                    : (e, value) => onCellClick(e, { timeUnit, resourceCategoryId: value.resourceCategoryId }),
                getCellColor: getCellColor(timeUnit, rowData[0]),
                columnVerticalAlignment: 'top',
                selectedCell: (cellData) =>
                    selectedCellData?.timeUnit === timeUnit &&
                    cellData.resourceCategoryId === selectedCellData.resourceCategoryId,

                render: ({ data, resourceCategoryId, usableResources }) => (
                    <SellLimitCell
                        absolutePerTimeUnitLimit={data[timeUnit]?.absolutePerTimeUnitLimit}
                        disabled={getCellDisabledState({
                            allResourceCategoryData: rowData[0],
                            data,
                            resourceCategoryId,
                            timeUnit,
                            usableResources,
                        })}
                        sold={data[timeUnit]?.sold ?? 0}
                        usableResources={usableResources}
                    />
                ),
            })
        ),
    ];
};

const getCellColor =
    (timeUnit: string, allResourceCategoryData: SellLimitMatrixRowData) =>
    (rowData: SellLimitMatrixRowData): MatrixCellColor | undefined => {
        const { absolutePerTimeUnitLimit, sold } = rowData.data[timeUnit] || {};
        const isCellDisabled = getCellDisabledState({
            allResourceCategoryData,
            data: rowData.data,
            resourceCategoryId: rowData.resourceCategoryId,
            timeUnit,
            usableResources: rowData.usableResources,
        });

        if (isCellDisabled) {
            return {
                color: 'basic',
                intensity: 0.8,
            };
        }

        if (absolutePerTimeUnitLimit == null || sold == null) return;

        if (sold < absolutePerTimeUnitLimit) {
            return {
                color: 'success',
                intensity: 0.2,
            };
        } else if (sold > absolutePerTimeUnitLimit) {
            return {
                color: 'danger',
                intensity: 0.2,
            };
        }
        return {
            color: 'warning',
            intensity: 0.2,
        };
    };

const getCellDisabledState = ({
    allResourceCategoryData,
    data,
    resourceCategoryId,
    timeUnit,
    usableResources,
}: {
    allResourceCategoryData: SellLimitMatrixRowData;
    data: Record<string, SellLimitPerTimeUnitData>;
    resourceCategoryId: Optional<string>;
    timeUnit: string;
    usableResources: Optional<number>;
}) => {
    const { absolutePerTimeUnitLimit, sold, freeResources, soldInRateGroup, rateGroupAbsolutePerTimeUnitLimit } =
        data[timeUnit] || {};

    const areAllSpacesSold = sold != null && usableResources != null && sold >= usableResources;
    const isOutOfResources = freeResources != null && freeResources <= 0;
    const hasRateGroupLimitReached =
        rateGroupAbsolutePerTimeUnitLimit && soldInRateGroup
            ? soldInRateGroup >= rateGroupAbsolutePerTimeUnitLimit
            : false;

    const allResourcesCellData = allResourceCategoryData.data[timeUnit];
    const hasAllResourcesLimitReached =
        allResourcesCellData?.sold && allResourcesCellData?.absolutePerTimeUnitLimit
            ? allResourcesCellData.sold >= allResourcesCellData.absolutePerTimeUnitLimit
            : false;

    const isAllResourcesCategory = resourceCategoryId === null;

    if (sold != null && absolutePerTimeUnitLimit != null && sold >= absolutePerTimeUnitLimit) {
        return false;
    }

    return (
        areAllSpacesSold ||
        isOutOfResources ||
        hasRateGroupLimitReached ||
        (hasAllResourcesLimitReached && !isAllResourcesCategory)
    );
};
